/**
 * All Projects Board - Unified View of All Active Projects
 *
 * 6-Bucket Structure (Canonical Stages):
 * 1. Pending — Waiting to start production art
 * 2. Production Art — Creating proofs and awaiting approval
 * 3. Printing — Approved and printing
 * 4. Assembly — Prints done, assembling
 * 5. Shipping — Ready to ship
 * 6. Complete — Closed in JobBoss (last 14 days)
 *    - Shows warning indicator for quotes with incomplete milestones
 *
 * Design follows PA Queue and Assembly Queue patterns:
 * - KanbanOverview at top showing workflow
 * - Collapsible sections with colored borders
 * - Expandable project rows with View Details button
 */

import { useState, useEffect, useCallback, useMemo } from "react";

const API_BASE = import.meta.env.VITE_API_URL || "";

// Bucket configuration matching canonical stages
const STAGE_BUCKETS = {
  PENDING: { label: "Pending", color: "gray", order: 0 },
  PROD_ART: { label: "Production Art", color: "purple", order: 1 },
  PRINTING: { label: "Printing", color: "blue", order: 2 },
  ASSEMBLY: { label: "Assembly", color: "green", order: 3 },
  SHIPPING: { label: "Shipping", color: "orange", order: 4 },
  COMPLETE: { label: "Complete", color: "teal", order: 5 },
};

// Color classes for each bucket
const BUCKET_COLORS = {
  gray: { border: "border-gray-300", bg: "bg-gray-50", header: "bg-gray-100 text-gray-700", badge: "bg-gray-100 text-gray-700" },
  purple: { border: "border-purple-300", bg: "bg-purple-50", header: "bg-purple-100 text-purple-700", badge: "bg-purple-100 text-purple-700" },
  blue: { border: "border-blue-300", bg: "bg-blue-50", header: "bg-blue-100 text-blue-700", badge: "bg-blue-100 text-blue-700" },
  green: { border: "border-green-300", bg: "bg-green-50", header: "bg-green-100 text-green-700", badge: "bg-green-100 text-green-700" },
  orange: { border: "border-orange-300", bg: "bg-orange-50", header: "bg-orange-100 text-orange-700", badge: "bg-orange-100 text-orange-700" },
  teal: { border: "border-teal-300", bg: "bg-teal-50", header: "bg-teal-100 text-teal-700", badge: "bg-teal-100 text-teal-700" },
};

export default function UnifiedProjectBoard({ onSelectProject }) {
  // Bucket state
  const [buckets, setBuckets] = useState({
    PENDING: [],
    PROD_ART: [],
    PRINTING: [],
    ASSEMBLY: [],
    SHIPPING: [],
    COMPLETE: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Data freshness
  const [lastSync, setLastSync] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPM, setSelectedPM] = useState("");

  // Fetch board data
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = sessionStorage.getItem("msal.backendToken");

      const response = await fetch(`${API_BASE}/api/unified/board`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();

      // Group projects by stage (column)
      const grouped = {
        PENDING: [],
        PROD_ART: [],
        PRINTING: [],
        ASSEMBLY: [],
        SHIPPING: [],
        COMPLETE: [],
      };

      if (data.columns) {
        for (const [stage, projects] of Object.entries(data.columns)) {
          if (grouped[stage]) {
            // Transform to match expected format
            grouped[stage] = projects.map((p) => ({
              id: p.quote_number,
              quoteNumber: p.quote_number,
              projectName: p.project_name,
              customerName: p.customer_code,
              pm: p.pm,
              jobCount: p.total_job_count,
              totalQty: p.total_qty,
              shipDate: p.ship_date,
              isRush: p.has_rush,
              hasActiveTickets: p.has_active_tickets,
              activeWorkerCount: p.active_worker_count,
              progressPercent: p.progress_percent,
              stageBreakdown: p.stage_breakdown,
              urgency: p.urgency,
              daysUntilDue: p.days_until_due,
              // COMPLETE stage: flag for incomplete milestones (needs investigation)
              hasIncompleteMilestones: p.has_incomplete_milestones,
              incompleteMilestonesCount: p.incomplete_milestones_count,
            }));
          }
        }
      }

      setBuckets(grouped);
      setLastSync(new Date());
    } catch (err) {
      console.error("Error fetching unified board:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Toggle expand
  const toggleExpand = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  // Get unique PMs for filter
  const uniquePMs = useMemo(() => {
    const pms = new Set();
    Object.values(buckets).flat().forEach((p) => {
      if (p.pm) pms.add(p.pm);
    });
    return Array.from(pms).sort();
  }, [buckets]);

  // Filter projects
  const filteredBuckets = useMemo(() => {
    const filtered = {};
    for (const [stage, projects] of Object.entries(buckets)) {
      filtered[stage] = projects.filter((p) => {
        const matchesSearch =
          !searchQuery ||
          p.quoteNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.projectName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.customerName?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPM = !selectedPM || p.pm === selectedPM;
        return matchesSearch && matchesPM;
      });
    }
    return filtered;
  }, [buckets, searchQuery, selectedPM]);

  // Count totals
  const totalProjects = useMemo(() => {
    return Object.values(filteredBuckets).flat().length;
  }, [filteredBuckets]);

  if (loading && totalProjects === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading all projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-red-600">Error: {error}</div>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-100">
      {/* Header */}
      <div className="flex-none px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-gray-900">All Projects</h1>
            <span className="text-sm text-gray-500">{totalProjects} projects</span>
            {lastSync && (
              <span className="text-xs text-gray-400">
                Synced {lastSync.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Search */}
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-48"
            />
            {/* PM Filter */}
            <select
              value={selectedPM}
              onChange={(e) => setSelectedPM(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All PMs</option>
              {uniquePMs.map((pm) => (
                <option key={pm} value={pm}>{pm}</option>
              ))}
            </select>
            {/* Refresh */}
            <button
              onClick={fetchData}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <svg className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Kanban Overview - Compact pipeline */}
      <KanbanOverview buckets={filteredBuckets} onSelectProject={onSelectProject} />

      {/* Main Content - Scrollable sections */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {Object.entries(STAGE_BUCKETS).map(([stage, config]) => (
          <Section
            key={stage}
            label={config.label}
            color={config.color}
            count={filteredBuckets[stage]?.length || 0}
          >
            {filteredBuckets[stage]?.length === 0 ? (
              <div className="py-4 text-center text-gray-400 text-sm">No projects</div>
            ) : (
              <ProjectList
                projects={filteredBuckets[stage]}
                expandedId={expandedId}
                onToggle={toggleExpand}
                onSelectProject={onSelectProject}
              />
            )}
          </Section>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   KANBAN OVERVIEW - Matches PA View and Assembly View format exactly
───────────────────────────────────────────────────────────────────────────── */

function KanbanOverview({ buckets, onSelectProject }) {
  const stages = Object.keys(STAGE_BUCKETS);

  return (
    <div className="flex-none px-4 py-3 bg-white border-b border-gray-200">
      {/* Workflow label */}
      <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
        <span>Project Workflow</span>
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent" />
      </div>

      <div className="flex items-stretch">
        {stages.map((stage, index) => {
          const config = STAGE_BUCKETS[stage];
          const projects = buckets[stage] || [];
          const isLast = index === stages.length - 1;

          return (
            <div key={stage} className="flex items-stretch flex-1 min-w-0">
              <KanbanColumn
                title={config.label}
                color={config.color}
                projects={projects}
                onSelectProject={onSelectProject}
                stepNumber={index + 1}
              />
              {/* Arrow between columns */}
              {!isLast && (
                <div className="flex items-center px-1 text-gray-300">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KanbanColumn({ title, color, projects, onSelectProject, stepNumber }) {
  const colorClasses = {
    gray: { header: 'bg-gray-100 text-gray-700 border-gray-200', body: 'bg-gray-50', accent: 'bg-gray-400' },
    purple: { header: 'bg-purple-100 text-purple-800 border-purple-200', body: 'bg-purple-50/30', accent: 'bg-purple-500' },
    blue: { header: 'bg-blue-100 text-blue-800 border-blue-200', body: 'bg-blue-50/30', accent: 'bg-blue-500' },
    green: { header: 'bg-green-100 text-green-800 border-green-200', body: 'bg-green-50/30', accent: 'bg-green-500' },
    orange: { header: 'bg-orange-100 text-orange-800 border-orange-200', body: 'bg-orange-50/30', accent: 'bg-orange-500' },
    teal: { header: 'bg-teal-100 text-teal-800 border-teal-200', body: 'bg-teal-50/30', accent: 'bg-teal-500' },
  };

  const colors = colorClasses[color] || colorClasses.gray;

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      {/* Column header */}
      <div className={`px-3 py-2 rounded-t-lg border ${colors.header} ${colors.body}`}>
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full ${colors.accent} text-white text-[10px] font-bold flex items-center justify-center`}>
            {stepNumber}
          </span>
          <span className="text-xs font-bold truncate">{title}</span>
        </div>
        <div className="text-xs font-medium mt-1 opacity-70">{projects.length} project{projects.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Column body - show project rows with scrolling */}
      <div className={`flex-1 border border-t-0 border-gray-200 rounded-b-lg px-1.5 py-1.5 space-y-0.5 ${colors.body} max-h-[280px] overflow-y-auto`}>
        {projects.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-3 italic">No projects</div>
        ) : (
          projects.map(p => (
            <KanbanRow key={p.id} project={p} onSelect={onSelectProject} />
          ))
        )}
      </div>
    </div>
  );
}

function KanbanRow({ project, onSelect }) {
  return (
    <div
      onClick={() => onSelect?.(project)}
      className="flex items-center gap-1.5 px-1.5 py-0.5 rounded cursor-pointer hover:bg-white transition-colors text-[10px]"
    >
      {project.isRush && (
        <span className="px-1 py-0.5 bg-red-100 text-red-600 text-[8px] rounded font-bold">RUSH</span>
      )}
      {project.hasIncompleteMilestones && (
        <span className="px-1 py-0.5 bg-amber-100 text-amber-700 text-[8px] rounded font-bold" title="Incomplete milestones - needs investigation">!</span>
      )}
      <span className="font-mono text-purple-600 font-medium w-12 flex-shrink-0">{project.quoteNumber}</span>
      <span className="text-gray-700 truncate flex-1 font-medium">{project.projectName}</span>
      <span className="text-blue-600 font-semibold w-6 text-right" title="Jobs">{project.jobCount || 0}</span>
      <span className="text-indigo-600 font-semibold w-10 text-right" title="Qty">{project.totalQty?.toLocaleString() || 0}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SECTION - Collapsible bucket section
───────────────────────────────────────────────────────────────────────────── */

function Section({ label, color, count, children }) {
  const [collapsed, setCollapsed] = useState(false);
  const colors = BUCKET_COLORS[color];

  return (
    <div className={`rounded-lg border ${colors.border} bg-white overflow-hidden`}>
      {/* Section Header */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        className={`flex items-center justify-between px-4 py-2 cursor-pointer ${colors.header}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{label}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
            {count}
          </span>
        </div>
        <span className="text-xs">{collapsed ? "▼" : "▲"}</span>
      </div>
      {/* Section Content */}
      {!collapsed && <div>{children}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   PROJECT LIST & ROW
───────────────────────────────────────────────────────────────────────────── */

function ProjectList({ projects, expandedId, onToggle, onSelectProject }) {
  return (
    <div className="divide-y divide-gray-100">
      {/* Header row */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs text-gray-500 font-medium bg-gray-50">
        <div className="col-span-1">Quote</div>
        <div className="col-span-4">Project</div>
        <div className="col-span-2 text-center">PM</div>
        <div className="col-span-1 text-center">Jobs</div>
        <div className="col-span-2 text-center">Progress</div>
        <div className="col-span-2 text-right">Ship Date</div>
      </div>
      {/* Project rows */}
      {projects.map((project) => (
        <ProjectRow
          key={project.id}
          project={project}
          isExpanded={expandedId === project.id}
          onToggle={() => onToggle(project.id)}
          onSelectProject={onSelectProject}
        />
      ))}
    </div>
  );
}

function ProjectRow({ project, isExpanded, onToggle, onSelectProject }) {
  const isOverdue = project.daysUntilDue !== null && project.daysUntilDue < 0;

  const formatShipDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div>
      {/* Main row */}
      <div
        onClick={onToggle}
        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer transition-colors hover:bg-gray-50 ${
          isExpanded ? "bg-blue-50/30" : ""
        }`}
      >
        {/* Quote Number + Rush/Warning indicators */}
        <div className="col-span-1 flex items-center gap-1.5">
          {project.isRush && (
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 animate-pulse" title="Rush" />
          )}
          {project.hasIncompleteMilestones && (
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Incomplete milestones - needs investigation" />
          )}
          <span className="font-mono text-xs text-purple-600 font-medium">
            {project.quoteNumber}
          </span>
        </div>

        {/* Project Name */}
        <div className="col-span-4">
          <span className="text-sm text-gray-900 truncate block font-medium">{project.projectName}</span>
          {project.customerName && (
            <span className="text-xs text-gray-500 truncate block">{project.customerName}</span>
          )}
        </div>

        {/* PM */}
        <div className="col-span-2 text-center">
          {project.pm ? (
            <span className="text-xs text-gray-700 font-medium">{project.pm}</span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>

        {/* Job Count */}
        <div className="col-span-1 text-center">
          <span className="text-sm text-blue-600 font-semibold">{project.jobCount || 0}</span>
        </div>

        {/* Progress */}
        <div className="col-span-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full"
                style={{ width: `${project.progressPercent || 0}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{project.progressPercent || 0}%</span>
          </div>
        </div>

        {/* Ship Date */}
        <div className="col-span-2 text-right flex items-center justify-end gap-2">
          {project.shipDate ? (
            <span className={`text-xs font-medium ${isOverdue ? "text-red-600" : "text-gray-600"}`}>
              {formatShipDate(project.shipDate)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
          <span className="text-gray-400 text-[10px]">{isExpanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="bg-white border-t border-gray-200 px-4 py-3">
          <div className="ml-4">
            {/* Incomplete milestones warning */}
            {project.hasIncompleteMilestones && (
              <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm font-medium">
                    Incomplete Milestones - {project.incompleteMilestonesCount || 0} job(s) need investigation
                  </span>
                </div>
                <p className="text-xs text-amber-600 mt-1">
                  Order closed but critical milestone operations (PROOF-PDF, PDF APPROVAL, PRINTS DONE, or POP/PACKOUT) were not completed.
                </p>
              </div>
            )}
            {/* Action buttons */}
            <div className="flex gap-2 mb-3">
              {onSelectProject && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectProject(project);
                  }}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Details
                </button>
              )}
              {project.hasActiveTickets && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                  {project.activeWorkerCount || 1} active
                </span>
              )}
            </div>

            {/* Stage breakdown */}
            {project.stageBreakdown && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(project.stageBreakdown).map(([stage, count]) => {
                  if (count === 0) return null;
                  const config = STAGE_BUCKETS[stage];
                  const colors = config ? BUCKET_COLORS[config.color] : BUCKET_COLORS.gray;
                  return (
                    <span key={stage} className={`px-2 py-0.5 rounded text-xs font-medium ${colors.badge}`}>
                      {config?.label || stage}: {count}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { STAGE_BUCKETS, BUCKET_COLORS };
